import './Labels.scss';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';

import Logger from '@KrashidBuilt/common/utils/logger';
import { Colors, ButtonType, Orientation } from '@fifth-gear-tech/common-components/dist/utils/StyledTheme';
import { Button, FormSection, FormSectionLabel, FormContainer, InputField, RadioGroup, SelectField } from '@fifth-gear-tech/common-components/dist/components';
import { Checkbox, FormControl, FormControlLabel, Grid, Switch, Typography } from '@material-ui/core';

import { Loading, LinkedList, ErrorMessage, ErpLinkButton, QrCodeIcon, FeatureSpotlight, PrintRemnantLabelModal, PrintJobSheetModal } from '../../components';
import { FaceLabel } from '../../containers';
import api from '../../api';
import utils from '../../utils';
import helpers from '../../helpers';
import { CACHE_KEY, FEATURES, IS_BAD_CHROME, LABEL_SIZE_MAP, LABEL_SIZE_OPTIONS, SPEEDLABEL_WEB_API_BASE } from '../../constants';

import { UserContext } from '../../ctx';
import { ROUTE_MAP } from '../../routes';

const logger = new Logger('Labels');

/**
 * Labels Component
 * @augments {Component<Props, State>}
 */
class Labels extends Component {

    constructor(props, context) {
        super(props);

        let { match: { params: { jobId } } } = this.props;

        if (!jobId) {
            jobId = JSON.parse(localStorage.getItem(CACHE_KEY.SPEEDLABEL_JOB_ID));
        }

        this.mainInputRef = React.createRef();

        this.state = {
            // api
            loaded: false,
            working: false,
            // speedlink
            erpLink: null,
            hashId: null,
            dId: context?.user?.dId,
            // local storage options
            qrCodeLabelsDisabled: localStorage.getItem(CACHE_KEY.QR_CODE_LABELS_DISABLED) ? JSON.parse(localStorage.getItem(CACHE_KEY.QR_CODE_LABELS_DISABLED)) : {},
            qrHintDisabled: localStorage.getItem(CACHE_KEY.QR_CODE_HINT_DISABLED) === 'true' ? true : false,
            remnantsDisabled: localStorage.getItem(CACHE_KEY.REMNANTS_DISABLED) === 'true' ? true : false,
            // global feature flags 
            hasRemnantLabelFeature: () => context?.user?.hasFeature?.(FEATURES.REMNANT_LABELS),
            hasJobSheetFeature: () => context?.user?.hasFeature?.(FEATURES.JOB_SHEET),
            // labels
            error: null,
            jobId,
            job: null,
            parts: [],
            filter: {},
            labelSize: localStorage.getItem(CACHE_KEY.LABEL_SIZE) || LABEL_SIZE_MAP.DEFAULT,
            templates: [],
            checklists: [],
            colors: [],
            template: null,
            checklist: null,
            selectedColors: [],
            // remnants
            manualPrintMode: false,
            remTemplate: null,
            // job sheet
            jobSheetTemplate: null
        };
    }

    load = () => {

        const { loaded, working } = this.state;
        if (!loaded && !working) {
            this.setState({ working: true }, async () => {

                try {

                    const { jobId } = this.state;

                    if (jobId) {
                        this.loadJobAndParts();
                    }

                    const promises = [];

                    promises.push(api.templates.getAll());
                    promises.push(api.checklists.getAll());
                    promises.push(api.colors.getAll());
                    promises.push(api.templates.getRemTemplate());
                    promises.push(api.templates.getJobSheetTemplate());

                    // catch errors and return empty array
                    const [templates, checklists, colors, remTemplate, jobSheetTemplate] = await Promise.all(promises.map((p) => p.catch((e) => {
                        logger.error(e); return [];
                    })));

                    const cache = {
                        template: JSON.parse(localStorage.getItem(CACHE_KEY.TEMPLATE_SELECTION)),
                        checklist: JSON.parse(localStorage.getItem(CACHE_KEY.CHECKLIST_SELECTION)),
                        selectedColors: JSON.parse(localStorage.getItem(CACHE_KEY.COLOR_SELECTION)) || [],
                    };

                    // make sure that these cached items exist and are current
                    if (cache.template) {
                        const foundTemplate = utils.options.getSelected(templates, cache.template);
                        if (foundTemplate) {
                            cache.template = { label: foundTemplate.name, value: foundTemplate.id };
                        }
                    }

                    if (cache.checklist) {
                        const foundChecklist = utils.options.getSelected(checklists, cache.checklist);
                        if (foundChecklist) {
                            cache.checklist = { label: foundChecklist.name, value: foundChecklist.id };
                        }
                    }

                    this.setState({
                        working: false,
                        loaded: true,
                        error: null,
                        templates,
                        checklists,
                        colors,
                        remTemplate,
                        jobSheetTemplate,
                        ...cache
                    });

                } catch (error) {
                    this.setState({ working: false, loaded: true, error });
                }

            });
        }
    }

    componentDidMount() {
        this.load();
        window.addEventListener('beforeprint', this.handlePrint);
    }

    componentWillUnmount() {
        window.removeEventListener('beforeprint', this.handlePrint);
    }

    inputFocusCallback = () => {
        if (this.mainInputRef.current) {
            this.mainInputRef.current.focus();
        }
    }

    loadJobAndParts = async (refreshCache = false) => {
        const { jobId, labelSize } = this.state;

        this.setState({ working: true });

        if (jobId) {

            let speed_link;
            try {
                const promises = [];
                // check of button is supposed to refresh cache
                if (refreshCache) {
                    promises.push(api.jobs.refreshCacheAndGet({ jobId }));
                } else {
                    promises.push(api.jobs.getOne({ jobId }));

                }
                promises.push(api.parts.getAll({ jobId }));
                const [job, parts] = await Promise.all(promises);

                // load speed link only if job exists
                if (job) {
                    speed_link = await api.speed_link.getSpeedLink({ jobId });
                }

                this.setState({
                    job,
                    parts: parts.map((p) => Object.assign(p, { size: labelSize })),
                    error: null,
                    erpLink: speed_link?.erpLink,
                    hashId: speed_link?.id,
                    working: false,
                }, this.inputFocusCallback);
            } catch (error) {
                this.setState({ job: null, parts: [], error, working: false });
            }
        }
    }

    setJobId = (e) => {
        this.setState({ jobId: e, job: null, parts: [] }, () => {
            const { jobId } = this.state;
            localStorage.setItem(CACHE_KEY.SPEEDLABEL_JOB_ID, JSON.stringify(jobId));
            this.inputFocusCallback();
        });
    }

    onLabelSizeChange = (part, size) => {
        const { parts } = this.state;
        const idx = parts.findIndex((p) => p.id === part.id);
        const cloned = JSON.parse(JSON.stringify(parts));
        cloned[idx].size = size;
        this.setState({ parts: cloned });
    }

    getSelectedChecklist = () => {
        const { checklists, checklist, jobId } = this.state;
        logger.extra({ checklist, checklists, jobId });
        const found = checklist && checklist.value ? checklists.find((c) => c.id === checklist.value) : {};
        if (found) {
            return found.json_data;
        }
    }

    getSelectedColors = () => {
        const { selectedColors } = this.state;
        let array = [];

        try {
            for (let i = 0; i < selectedColors?.length; i++) {
                const c = selectedColors[i];
                if (c && c.selectedColorBand && c.selectedColorBand.selectedOption) {
                    const { label, value, visible } = c.selectedColorBand.selectedOption;
                    array.push({ label: visible ? label : '', style: { backgroundColor: `#${value}`, color: utils.color.isLight(value) ? '#000' : '#FFF' } });
                }
            }
        } catch (error) {
            logger.error(error);
        }

        return array;
    }

    onAllLabelSizeChange = (size) => {
        const { parts } = this.state;
        this.setState({ parts: parts.map((p) => Object.assign(p, { size })), labelSize: size });
        localStorage.setItem(CACHE_KEY.LABEL_SIZE, size);
    }

    handlePrint = async () => {
        const { templates, template, checklists, checklist, parts, filter, selectedColors } = this.state;

        try {

            const _template = template && template.value ? templates.find((x) => x.id === template.value) : false;
            const _checklist = checklist && checklist.value ? checklists.find((c) => c.id === checklist.value) : false;
            const _color = selectedColors && selectedColors?.length >= 1 && selectedColors[0].selectedColorBand ? selectedColors[0].selectedColorBand : false;
            const _parts = helpers.parts.getFiltered(parts, filter);

            const list = _parts.reduce((acc, { id, job_id, size, type }) => {
                if (type == 'remnant') {
                    return acc;
                } else {
                    return [...acc, {
                        part_id: id,
                        job_id,
                        template_id: _template.id,
                        color_id: _color ? _color.id : false,
                        checklist_id: _checklist ? _checklist.id : false,
                        label_type: size
                    }];
                }
            }, []);

            await api.print.capture(list);
        } catch (error) {
            logger.error(error);
            // only capture bad print POST if parts were actually sent
            if (parts.length > 0) {
                Sentry.captureException(error);
            }
        }

    }

    generateSelectColors = () => {
        const { selectedColors, colors } = this.state;
        return selectedColors.map((c, idx) => (
            <div key={idx} className={'ColorBandContainer'}>
                <div className={'ColorBandSelectListContainer'}>

                    <SelectField
                        label={'Color Band'}
                        placeholder={'Select band...'}
                        options={utils.options.getOptions(colors)}
                        value={c.selectedOption}
                        onChange={(selectedOption) => {
                            const { selectedColors } = this.state;
                            const cloned = JSON.parse(JSON.stringify(selectedColors));
                            cloned[idx].selectedOption = selectedOption;
                            cloned[idx].selectedColorBand = colors.find((cb) => cb.id === selectedOption.value);
                            this.setState({ selectedColors: cloned }, () => {
                                localStorage.setItem(CACHE_KEY.COLOR_SELECTION, JSON.stringify(cloned));
                            });
                        }}
                    />
                    {c.selectedColorBand && (
                        <SelectField
                            label={'Color Field'}
                            placeholder={'Select field...'}
                            options={c.selectedColorBand.json_data.items}
                            value={c.selectedColorBand.selectedOption}
                            onChange={(selectedOption) => {
                                const { selectedColors } = this.state;
                                const cloned = JSON.parse(JSON.stringify(selectedColors));
                                cloned[idx].selectedColorBand.selectedOption = selectedOption;
                                cloned[idx].selectedColorBand.selectedColor = c.selectedColorBand.json_data.items.find((c) => {
                                    c.id === selectedOption.value;
                                });
                                this.setState({ selectedColors: cloned }, () => {
                                    localStorage.setItem(CACHE_KEY.COLOR_SELECTION, JSON.stringify(cloned));
                                });
                            }}
                        />


                    )}
                </div>
                <Button
                    label={''}
                    type={ButtonType.Remove}
                    onClick={() => {
                        this.setState({ selectedColors: selectedColors.filter((_, index) => index !== idx) }, () => {
                            localStorage.setItem(CACHE_KEY.COLOR_SELECTION, JSON.stringify(null));
                        });
                    }} />
            </div>
        ));
    }

    onQrCodeDisabledChange = ({ target: { checked, name } }) => {
        const { qrCodeLabelsDisabled } = this.state;
        const disabled = { ...qrCodeLabelsDisabled, [name]: checked };
        this.setState({ qrCodeLabelsDisabled: disabled }, () => {
            localStorage.setItem(CACHE_KEY.QR_CODE_LABELS_DISABLED, JSON.stringify(disabled));
        });
    }

    toggleQrCodeHint = () => {
        const { qrHintDisabled } = this.state;
        this.setState({ qrHintDisabled: !qrHintDisabled }, () => {
            localStorage.setItem(CACHE_KEY.QR_CODE_HINT_DISABLED, !qrHintDisabled);
        });
    }

    setRemnantLabels = (remnantLabels) => {
        this.setState({ parts: remnantLabels });
    }

    toggleRemnantLabels = () => {
        const { remnantsDisabled } = this.state;
        this.setState({ remnantsDisabled: !remnantsDisabled }, () => {
            localStorage.setItem(CACHE_KEY.REMNANTS_DISABLED, !remnantsDisabled);
        });
    }

    render() {
        logger.extra({ _: this });
        const {
            // api state
            working,
            // speedlink
            erpLink,
            hashId,
            dId,
            // local storage options
            qrCodeLabelsDisabled,
            qrHintDisabled,
            remnantsDisabled,
            // global feature flags
            hasRemnantLabelFeature,
            hasJobSheetFeature,
            // labels
            error,
            jobId,
            job,
            parts,
            filter,
            labelSize,
            templates,
            checklists,
            template,
            checklist,
            selectedColors,
            colors,
            // remnants
            manualPrintMode,
            remTemplate,
            // job sheet
            jobSheetTemplate
        } = this.state;


        const foundTemplate = templates.find((ts) => ts?.id === template?.value);
        const barcodeEnabled = foundTemplate?.json_data?.barcodeEnabled;

        if (working) {
            return (<Loading />);
        }

        if (!job) {
            return (
                <div className={'Labels'}>
                    <FormContainer
                        title={''}
                        onSubmit={(e) => {
                            e.preventDefault();
                            this.loadJobAndParts();
                        }}
                        buttons={[
                            <Button
                                form={true}
                                key={'load'}
                                label={'Load Labels'}
                                type={ButtonType.Search}
                                colorTheme={Colors.Orange}
                            />
                        ]}
                        colorTheme={Colors.Grey}
                    >
                        <FormSection
                            label={'Info'}
                            subtext={'Enter an ID to get started'}
                            colorTheme={Colors.Grey}
                            orientation={Orientation.Mobile}
                        >

                            <InputField
                                key={'mainInput'}
                                ref={this.mainInputRef}
                                label={'Id'}
                                defaultValue={jobId || ''}
                                onChange={(e) => this.setJobId(e.target.value)}
                                clearIcon={true}
                                placeholder={'ex. 123456'}
                                onIconClick={() => this.setJobId('')}
                            />

                            {error && (
                                <ErrorMessage error={error} />
                            )}

                        </FormSection>
                    </FormContainer>
                </div>
            );
        }

        const checklistSelection = this.getSelectedChecklist();
        const colorSelection = this.getSelectedColors();

        // get label fields using the selected template, the calc selected, and the job data
        const fields = helpers.parser.getValues(job, utils.options.getSelected(templates, template), filter);
        const remnantFields = helpers.parser.getValues(job, remTemplate || utils.options.getSelected(templates, template), filter);

        const filtered = helpers.parts.getFiltered(parts, filter);

        // remnant not disabled locally or by global feature flag
        const shouldRenderRemnant = hasRemnantLabelFeature() && !remnantsDisabled && parts.length > 0 && filtered.length > 0 && !manualPrintMode;
        const remnantLabel = utils.remnant.partToRemnant(filtered[0], 'r_0');

        logger.info({ _: this, parts, fields, filtered });

        const printButton = template && parts.length > 0 ? <Button
            key={'Print'}
            className={'PrintLabelButton'}
            label={'Print Labels'}
            onClick={window.print}
            type={ButtonType.Dashboard}
            colorTheme={Colors.Orange}
        /> : null;

        return (
            <div className={'Labels'}>
                <FormContainer title={''} buttons={[printButton]}>

                    <FormSection
                        label={'Info'}
                        subtext={'Enter an ID to get started'}
                        colorTheme={Colors.Grey}
                        orientation={Orientation.Mobile}
                    >

                        <InputField
                            ref={this.mainInputRef}
                            label={'Id'}
                            defaultValue={jobId || ''}
                            onChange={(e) => {
                                this.setJobId(e.target.value);
                            }}
                            clearIcon={true}
                            placeholder={'ex. 123456'}
                            onIconClick={() => this.setJobId('')}
                        />
                        {error && (
                            <ErrorMessage error={error} />
                        )}

                        <LinkedList url={`${SPEEDLABEL_WEB_API_BASE}/api/private/jobs/${jobId}/options`} onChange={(filter) => {
                            this.setState({ filter });
                        }} />

                        {erpLink && <ErpLinkButton dId={dId} onClick={() => window.open(erpLink, '_blank')}></ErpLinkButton>}


                        <div title='Refresh Job'>
                            <Button
                                type={ButtonType.Update}
                                //  sage or grey
                                colorTheme={ButtonType.Id}
                                label={''}
                                className={'refreshButton'}
                                onClick={() => this.loadJobAndParts(true)}
                            />
                        </div>

                    </FormSection>

                    <FormSection orientation={Orientation.Mobile}>
                        <FormSectionLabel label={'Label Size'} subtext={''} buttons={[]} />

                        {/* this only renders if user has not been educated */}
                        <FeatureSpotlight
                            // styles={{ padding: '10rem' }}
                            feature={FEATURES.QR_PART_IMAGES}
                            canRender={() => parts.length > 0 && template}
                            step={1}
                            header={'👋 Check this out!'}
                            body={<span><strong>4x6</strong>, <strong>4x3</strong> and <strong>4x1</strong> labels have <strong>scannable QR codes</strong> on them now!</span>}
                            onDismiss={() => {
                                // force enable 4x6
                                this.setState({ labelSize: LABEL_SIZE_MAP._4x6 });
                                this.onAllLabelSizeChange(LABEL_SIZE_MAP._4x6);
                                // force enable qr code
                                this.setState({ qrHintDisabled: false }, () => {
                                    localStorage.setItem(CACHE_KEY.QR_CODE_HINT_DISABLED, false);
                                });
                                this.setState({ qrCodeLabelsDisabled: {} }, () => {
                                    localStorage.setItem(CACHE_KEY.QR_CODE_LABELS_DISABLED, JSON.stringify({}));
                                });
                            }}
                        />

                        <RadioGroup
                            options={IS_BAD_CHROME ? ['4x8', '4x6', '4x1'] : ['4x8', '4x6', '4x3', '4x1']}
                            onValueChange={this.onAllLabelSizeChange}
                            colorTheme={Colors.Orange}
                            value={labelSize}
                        />
                    </FormSection>

                    {/* QR Code for every part label image? */}
                    <FormSection orientation={Orientation.Mobile}>
                        <FormSectionLabel label={<span>Label QR Code <QrCodeIcon height={25} className={'QrCodeIcon'} /></span>} subtext={'Hide QR code on labels'} />

                        <Grid container style={{ marginTop: '0.5rem' }}>
                            {LABEL_SIZE_OPTIONS.map(({ value, label }) => (
                                <Grid item key={value} xs={6}>
                                    <Typography variant='body2' style={{ marginLeft: '-0.7rem', marginRight: '1.5rem' }}>
                                        <Checkbox size='large' color='black' name={label} checked={qrCodeLabelsDisabled[value]} onChange={(e) => this.onQrCodeDisabledChange(e)} />
                                        {label}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>

                        <FormControl>
                            <FormControlLabel control={<Switch color={'primary'} />} label={<Typography variant={'body2'}>Hide all hints</Typography>} checked={qrHintDisabled} onChange={() => this.toggleQrCodeHint()} />
                        </FormControl>
                    </FormSection>

                    <FeatureSpotlight
                        tilt={false}
                        tail={false}
                        // styles={{ padding: '10rem' }}
                        feature={FEATURES.QR_PART_IMAGES}
                        canRender={() => parts.length > 0 && template}
                        step={0}
                        header={'🚨 New SPEEDlabel Feature!'}
                        styles={{ padding: '10rem' }}
                        body={<div>
                            <iframe width="500" height="500" allowFullScreen frameBorder="0" title='qr-code-yt-embed'
                                src="https://www.youtube.com/embed/xgX3siskKqs?rel=0&mute=1&autoplay=1&loop=1&playlist=xgX3siskKqs"
                                allow="autoplay; encrypted-media"
                            />
                        </div>}
                        onDismiss={() => {
                            // force enable 4x6
                            this.setState({ labelSize: LABEL_SIZE_MAP._4x6 });
                            this.onAllLabelSizeChange(LABEL_SIZE_MAP._4x6);
                            // force enable qr code
                            this.setState({ qrHintDisabled: false }, () => {
                                localStorage.setItem(CACHE_KEY.QR_CODE_HINT_DISABLED, false);
                            });
                            this.setState({ qrCodeLabelsDisabled: {} }, () => {
                                localStorage.setItem(CACHE_KEY.QR_CODE_LABELS_DISABLED, JSON.stringify({}));
                            });
                        }}
                    />

                    <FormSection label={'Label Details'} subtext={'Select the template and checklist'} orientation={Orientation.Mobile}>
                        <SelectField
                            label={'Template'}
                            placeholder={'Select template...'}
                            options={utils.options.getOptions(templates)}
                            value={template}
                            onChange={(template) => {
                                this.setState({ template }, () => {
                                    localStorage.setItem(CACHE_KEY.TEMPLATE_SELECTION, JSON.stringify(template));
                                });
                            }}
                        />

                        <SelectField
                            label={'Side Form'}
                            placeholder={'Select Side Form...'}
                            options={utils.options.getOptions(checklists)}
                            value={checklist}
                            onChange={(checklist) => {
                                this.setState({ checklist }, () => {
                                    localStorage.setItem(CACHE_KEY.CHECKLIST_SELECTION, JSON.stringify(checklist));
                                });
                            }}
                        />
                    </FormSection>


                    <FormSection label={'Color Band'} subtext={'Add or modify color bands to be shown on the label'} orientation={Orientation.Mobile}>
                        <Button
                            className={'ColorBandButton'}
                            label={'Add Color Band'}
                            onClick={() => {
                                this.setState({ selectedColors: selectedColors.concat([{}]) });
                            }}
                            colorTheme={Colors.Green}
                            type={ButtonType.Add}
                        />

                        {this.generateSelectColors()}
                    </FormSection>

                    {hasJobSheetFeature() &&
                        <FormSection label={'Job Sheet'} subtext={'Print an 8.5x11 job sheet'} orientation={Orientation.Mobile}>
                            {
                                jobSheetTemplate && parts.length > 0
                                    ? <PrintJobSheetModal jobId={jobId} filter={filter} colors={colors} checklists={checklists} part={parts[0]} hashId={hashId} template={jobSheetTemplate} />
                                    : (
                                        <Button colorTheme={Colors.Red} type={ButtonType.Id}
                                            onClick={() => {
                                                this.props.history.push(ROUTE_MAP.SETTINGS.JOB_SHEET_TEMPLATE);
                                                // setTimeout(() => { this.props.history.push(ROUTE_MAP.SETTINGS.JOB_SHEET_TEMPLATE)}, 1000)
                                            }}
                                            label={<Typography variant={'subtitle2'} style={{ letterSpacing: '0.1rem' }}>Setup Template</Typography>}
                                        />
                                    )
                            }
                        </FormSection>
                    }


                    {/* print extra remnant labels */}
                    {hasRemnantLabelFeature() &&
                        <FormSection label={'Remnant Labels'} subtext={'Disable or print extra remnant labels'} orientation={Orientation.Mobile}>
                            <FormControl>
                                <FormControlLabel control={<Switch color={'primary'} />} label={<Typography variant={'body2'}>Disable remnant labels</Typography>} checked={remnantsDisabled} onChange={() => this.toggleRemnantLabels()} />
                            </FormControl>
                            {filtered.length > 0 && template
                                ? (
                                    <PrintRemnantLabelModal
                                        {...{ ...this.state, fields: remnantFields, filtered }}
                                        setRemnantLabels={this.setRemnantLabels}
                                        resetLabels={this.loadJobAndParts}
                                        togglePrintMode={() => this.setState({ manualPrintMode: !manualPrintMode })}
                                        colors={colors}
                                    />
                                )
                                : (
                                    <div>
                                        <Typography variant={'caption'}>(You can only print extra remnants with at least one part and template)</Typography>
                                    </div>
                                )
                            }
                        </FormSection>
                    }

                </FormContainer>

                <div className={'labels-container'}>
                    <FeatureSpotlight
                        feature={FEATURES.QR_PART_IMAGES}
                        canRender={() => parts.length > 0 && template}
                        step={2}
                        header={'📸 Scan with your phone!'}
                        body={<span>This <strong>scannable QR code</strong> will show you all your part images. Print one and test it out!</span>}
                        styles={{ marginLeft: '21rem', marginBottom: '-8rem' }} // precise spotlight positioning
                        onDismiss={() => {
                            window.print();
                        }}
                    />
                    {/* part labels */}
                    {template &&
                        filtered.map((p, idx) => (
                            <FaceLabel
                                colors={p?.colors || colorSelection} // use remnant label color override if it exists
                                key={p.id}
                                part={p}
                                fields={p?.type == 'remnant' ? remnantFields : fields}
                                checklist={checklistSelection}
                                partNumber={idx + 1}
                                totalParts={filtered?.length}
                                size={p.size}
                                onSizeChange={(size) => {
                                    this.onLabelSizeChange(p, size);
                                }}
                                barcodeEnabled={barcodeEnabled}
                                qrCodeLabelsDisabled={qrCodeLabelsDisabled}
                                qrHintDisabled={qrHintDisabled}
                                hashId={hashId}

                                // remnant labels stuff
                                allowSizeChange={p?.type == 'remnant' ? false : true}
                            />
                        ))}
                    {/* remnant label */}
                    {(remnantLabel && shouldRenderRemnant && !manualPrintMode) &&
                        <FaceLabel
                            colors={colorSelection}
                            part={remnantLabel}
                            fields={remnantFields}
                            size={LABEL_SIZE_MAP.REMNANT}
                            hashId={hashId}
                            allowSizeChange={false}
                        />
                    }
                </div>
            </div>
        );
    }
}

Labels.contextType = UserContext;

Labels.defaultProps = {
};

Labels.propTypes = {
    match: PropTypes.shape(),
    history: PropTypes.shape()
};


export default Labels;
